/**
 * หมัดหมอกวน (drift-fist): the caster's body through tf-supreme หมอกวนครอบฟ้า (the ท่าใหญ่; rules' table
 * packages/shared/src/data/longMoves/taijiFist.ts, keys tg…). SKELETON PASS (A4, speed mode): rough key poses on the long
 * kit, all hand-to-hand (no weapon arm, so no `ARMS`). The caster is ROOTED: no `lift`, no `rot`, feet barely move; the
 * waist turns through `view` only (45° steps from the heading held at the catch). TODO-polish: hand paths, the foe's
 * wrist contact, finer feet, per-key easing, anatomy check on every key.
 */
import type { Ease, LimbSpec } from '../kit';
import type { Pose } from '../index';
import { buildLong, type LongKey } from './kit';

export const ARMS = {} satisfies Record<string, LimbSpec>;

type H = [number, number];
/** A key: wide planted stance by default, both palms at the given places. */
const K = (A: H, B: H, o: Partial<LongKey> = {}): LongKey => ({
  feet: 1.7, f: [-8, 7], o: 9, tilt: 0, arm: 'forward', far: 'fwd', face: 'cast', A, B, ...o,
});

/** tgOpen (the 0.8 s tell): wide horse stance, both hands draw one slow circle, palms end facing down. */
const OPEN: LongKey[] = [
  K([8, 28], [8, 28], { hem: 1 }),
  K([12, 22], [-4, 22], { hem: 2, flare: 1, sway: -1 }),
  K([8, 12], [2, 12], { o: 7, hem: 3, flare: 2 }),
  K([-2, 18], [-8, 24], { hem: 3, flare: 3, sway: 1 }),
  K([10, 26], [10, 26], { o: 10, hem: 4, flare: 4 }),
  K([11, 27], [11, 27], { o: 10, hem: 4, flare: 4, sway: -0.5 }),
];
/** tgDraw: sink, both palms reel in to the chest. */
const DRAW: LongKey[] = [
  K([14, 22], [12, 24], { o: 10 }),
  K([10, 22], [8, 24], { o: 11, lean: -1 }),
  K([5, 22], [3, 24], { o: 12, lean: -1, hem: 2 }),
  K([6, 21], [4, 23], { o: 12 }),
];
/** tgYieldL / tgYieldR: waist turns, lead palm out stuck to the wrist, rear palm guides the elbow, feet planted. */
const YIELD = (s: 1 | -1): LongKey[] => [
  K([12, 22], [6, 25], { o: 10, turn: s }),
  K([14, 21], [4, 24], { o: 10, turn: 2 * s, view: 2 * s, sway: s, hem: 2 }),
  K([14, 20], [2, 23], { o: 10, turn: 3 * s, view: 4 * s, sway: 2 * s, hem: 3, flare: 2 }),
  K([13, 21], [3, 24], { o: 10, turn: s, view: 2 * s, hem: 2 }),
  K([12, 22], [6, 25], { o: 10, view: 0 }),
];
/** tgTurnBack: both palms press the foe's arm down, hips drop. */
const TURN_BACK: LongKey[] = [
  K([12, 24], [8, 26], { o: 11 }),
  K([13, 32], [9, 32], { o: 14, lean: 2, tilt: 8, hem: 2 }),
  K([14, 36], [10, 36], { o: 15, lean: 2, tilt: 10, hem: 3 }),
  K([14, 36], [10, 36], { o: 15, lean: 2, tilt: 10 }),
];
/** tgPalmRain: 8 alternating short palms, a half-step in (blur from the beat's `ai`). */
const PALM_RAIN: LongKey[] = [
  K([15, 24], [4, 26], { o: 10, lean: 1 }),
  K([4, 26], [15, 22], { o: 10, lean: 1 }),
  K([15, 22], [4, 26], { o: 10, lean: 2, f: [-8, 9] }),
  K([4, 26], [15, 24], { o: 10, lean: 2, f: [-8, 9] }),
  K([15, 24], [4, 26], { o: 10, lean: 2 }),
  K([4, 26], [15, 22], { o: 10, lean: 2 }),
  K([15, 22], [4, 26], { o: 10, lean: 2 }),
  K([14, 24], [10, 24], { o: 10, lean: 1 }),
];
/** tgRoot (quiet): knees deep, hands crossed at the dantian then drawn apart, breath in. */
const ROOT: LongKey[] = [
  K([8, 30], [8, 30], { o: 13, feet: 1.9 }),
  K([6, 31], [8, 31], { o: 15, feet: 1.9, face: 'block' }),
  K([-2, 28], [14, 28], { o: 15, feet: 1.9, face: 'block', flare: 2 }),
  K([-6, 24], [16, 24], { o: 15, feet: 1.9, hem: 2, flare: 3 }),
];
/** tgCrush: rear foot drives, a stride in, double push at the chest (held), then the palms sink. */
const CRUSH: LongKey[] = [
  K([-4, 24], [-6, 26], { o: 11, lean: -1, tilt: -4 }),
  K([8, 22], [6, 24], { o: 9, lean: 2, f: [-9, 10], tilt: 6 }),
  K([17, 21], [16, 23], { o: 8, lean: 3, f: [-10, 12], tilt: 10, hem: 3, flare: 4 }),
  K([17, 21], [16, 23], { o: 8, lean: 3, f: [-10, 12], tilt: 10, hem: 4, flare: 5 }),
  K([16, 25], [15, 27], { o: 9, lean: 2, f: [-10, 12], tilt: 6, hem: 2 }),
];
/** tgSettle: the push held, the palms sink slowly. */
const SETTLE: LongKey[] = [
  K([16, 24], [15, 26], { o: 9, lean: 2, f: [-10, 12], tilt: 4 }),
  K([14, 28], [13, 30], { o: 9, lean: 1, f: [-9, 11], tilt: 2 }),
  K([11, 32], [10, 33], { o: 8, f: [-8, 9] }),
];
/** tgStance: feet close, hands fold back. */
const STANCE: LongKey[] = [
  K([9, 31], [9, 31], { feet: 1.4, f: [-7, 6], o: 6 }),
  K([8, 30], [9, 30], { feet: 1.2, f: [-6, 5], o: 5, face: 'idle' }),
  K([7, 28], [8, 28], { feet: 1.2, f: [-6, 5], o: 4, face: 'idle' }),
];
/** tgSpent (whiff): the push into empty air, half a step stagger, hands drop. */
const SPENT: LongKey[] = [
  K([14, 22], [13, 24], { o: 9, lean: 2 }),
  K([17, 21], [16, 23], { o: 8, lean: 4, tilt: 14, f: [-8, 11] }),
  K([12, 30], [11, 32], { o: 10, lean: 3, tilt: 16, f: [-8, 11], face: 'block' }),
  K([8, 33], [8, 34], { o: 10, lean: 2, tilt: 10, f: [-8, 10], face: 'block', hunch: 1 }),
];

export type TaijiMotionId =
  | 'tgOpen' | 'tgDraw' | 'tgYieldL' | 'tgYieldR' | 'tgTurnBack' | 'tgPalmRain'
  | 'tgRoot' | 'tgCrush' | 'tgSettle' | 'tgStance' | 'tgSpent';
type N = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export type Poses = `${TaijiMotionId}_${N}`;

const built = buildLong<TaijiMotionId, Poses>('หมัดหมอกวน', {
  tgOpen: { keys: OPEN, from: 'ready' },
  tgDraw: { keys: DRAW, from: 'tgOpen_6', ease: ['in', 'out', 'inOut', 'inOut'] },
  tgYieldL: { keys: YIELD(1), from: 'tgDraw_4' },
  tgYieldR: { keys: YIELD(-1), from: 'tgYieldL_5' },
  tgTurnBack: { keys: TURN_BACK, from: 'tgYieldR_5', ease: ['out', 'inOut', 'inOut', 'inOut'] },
  tgPalmRain: { keys: PALM_RAIN, from: 'tgTurnBack_4', ease: Array(8).fill('inOut') as Ease[] },
  tgRoot: { keys: ROOT, from: 'tgPalmRain_8' },
  tgCrush: { keys: CRUSH, from: 'tgRoot_4', ease: ['in', 'in', 'out', 'inOut', 'inOut'] },
  tgSettle: { keys: SETTLE, from: 'tgCrush_5' },
  tgStance: { keys: STANCE, from: 'tgSettle_3', to: 'ready' },
  tgSpent: { keys: SPENT, from: 'tgOpen_6', to: 'ready', ease: ['in', 'out', 'inOut', 'inOut', 'inOut'] },
});

export const POSES = built.POSES;
export const MOTIONS = built.MOTIONS as unknown as readonly { id: TaijiMotionId; keys: readonly Pose[]; ease: readonly Ease[] }[];
export const CHANNELS = built.CHANNELS;
